/**
 * Helpers for prefilling the Create Task modal from a source message and for
 * translating the modal's local date-time value into the task-svc contract.
 */
import tzTime, { parseLocalDateTime } from './tzTime';

const TITLE_SNIPPET_MAX = 40;

/** Collapse whitespace so snippets built from message bodies stay on one line. */
function normalizeWhitespace(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/** Prefilled task title: `Follow up: <message snippet>`. */
export function buildTaskTitle(messageBody: string): string {
  const normalized = normalizeWhitespace(messageBody);
  const snippet =
    normalized.length > TITLE_SNIPPET_MAX
      ? `${normalized.slice(0, TITLE_SNIPPET_MAX).trimEnd()}...`
      : normalized;
  return `Follow up: ${snippet}`;
}

/** Prefilled description: `Context: "<quoted message>"`. */
export function buildTaskDescription(messageBody: string): string {
  return `Context: "${normalizeWhitespace(messageBody)}"`;
}

/**
 * Convert a `datetime-local` input value ("YYYY-MM-DDTHH:mm", local time) into
 * an RFC3339 UTC string for `due_at`. Returns null for empty input.
 */
export function toDueAtUtc(localDateTime: string): string | null {
  if (!localDateTime) return null;
  const date = parseLocalDateTime(localDateTime);
  return date.isValid() ? date.toISOString() : null;
}

/** True when a `datetime-local` value is a valid, future instant. */
export function isFutureDateTime(localDateTime: string, now: Date = new Date()): boolean {
  if (!localDateTime) return false;
  const date = parseLocalDateTime(localDateTime);
  return date.isValid() && date.valueOf() > now.getTime();
}

/** Convert an ISO date (or null) into a `datetime-local` input value, local time. */
export function toDatetimeLocal(isoDate: string | null): string {
  return isoDate ? tzTime(isoDate).format('YYYY-MM-DDTHH:mm') : '';
}
